/* eslint-disable graphql-relay-compat/no-apollo-components -- This file uses Mutation apollo component to create the apollo-relay component shim */
import React from 'react';
import type { OperationVariables } from '@apollo/react-common';
import { Mutation, type MutationComponentOptions } from 'react-apollo';

import { expValEquals } from '@confluence/feature-experiments';

import { useMutationInterop } from './useMutationInterop';

export function MutationInteropImpl<TData = any, TVariables = OperationVariables>({
	children,
	mutation,
	...props
}: MutationComponentOptions<TData, TVariables>): JSX.Element | null {
	const mutationTuple = useMutationInterop(mutation, props);
	return children(...mutationTuple);
}

export function MutationInterop<
	TData extends object = any,
	TVariables extends OperationVariables = OperationVariables,
>({ children, ...rest }: MutationComponentOptions<TData, TVariables>): JSX.Element | null {
	return expValEquals('confluence_deprecate_apollo_components', 'isEnabled', true) ? (
		<MutationInteropImpl<TData, TVariables> {...rest}>{children}</MutationInteropImpl>
	) : (
		/*
			We need to cast children to MutationComponentOptions['children'] type
			because children is a function that received the two generic types.

			However, the Mutation component cannot resolve the two generic types for the children prop without this typecast,
			leading to a type error:
			MutationFunctionOptions<TData, TVariables> is not assignable to type MutationFunctionOptions<any, OperationVariables>

			We can't type the Mutation component (Mutation<TData, TVariables>) directly
			because typescript ends up resolving the Mutation component to be without the mutation prop.
			Super weird issue that only occurs when using generic types.

			As such, this is a workaround to satisfy TypeScript's type checking.
		*/
		<Mutation {...rest}>{children as MutationComponentOptions['children']}</Mutation>
	);
}
